import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { dateLocale } from '../../i18n/locale';
import { localize } from '../../i18n/script';
import {
  reviewApi,
  ReviewApiError,
  type MyPickReview,
  type MyReview,
} from '../../services/reviewApi';
import PickReviewSheet from '../picks/PickReviewSheet';

import ReviewEditorModal from './ReviewEditorModal';

function formatDate(iso: string | null, language: string): string {
  if (!iso) return '';
  const locale = dateLocale(language);
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * The author's home for their reviews: status, edit (re-enters moderation),
 * delete, and resubmit of rejected ones. The activity detail page is read-only,
 * so all review management happens here.
 */
interface Props {
  /** Notify the parent (Dashboard) so the rejected-review badge stays in sync. */
  onChanged?: () => void;
}

export default function MyReviewsTab({ onChanged }: Props) {
  const { t, i18n } = useTranslation(['review', 'picks']);
  const [items, setItems] = useState<MyReview[]>([]);
  const [pickItems, setPickItems] = useState<MyPickReview[]>([]);
  const [editingPick, setEditingPick] = useState<MyPickReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MyReview | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      reviewApi
        .getMine()
        .then((res) => setItems(res.items))
        .catch(() => setItems([])),
      reviewApi
        .pickMine()
        .then((res) => setPickItems(res.items))
        .catch(() => setPickItems([])),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (review: MyReview) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    setBusyId(review.id);
    setError(null);
    try {
      await reviewApi.remove(review.id);
      setItems((prev) => prev.filter((r) => r.id !== review.id));
      onChanged?.();
    } catch (err) {
      setError(err instanceof ReviewApiError ? err.message : 'Error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeletePick = async (review: MyPickReview) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    setBusyId(review.id);
    setError(null);
    try {
      await reviewApi.pickRemove(review.id);
      setPickItems((prev) => prev.filter((r) => r.id !== review.id));
      onChanged?.();
    } catch (err) {
      setError(err instanceof ReviewApiError ? err.message : 'Error');
    } finally {
      setBusyId(null);
    }
  };

  const statusLabel = (s: MyReview['status']) =>
    s === 'approved'
      ? t('statusPublished')
      : s === 'rejected'
        ? t('statusRejected')
        : t('statusPending');

  const statusClass = (s: MyReview['status']) =>
    s === 'approved'
      ? 'bg-primary/10 text-primary'
      : s === 'rejected'
        ? 'bg-red-50 text-red-700'
        : 'bg-yellow-50 text-yellow-800';

  const title = (r: MyReview) => localize(r.activity.titleCN, r.activity.titleEN, i18n.language);

  if (loading) {
    return <p className="text-sm text-gray-500">{t('loading')}</p>;
  }

  if (items.length === 0 && pickItems.length === 0) {
    return <p className="text-sm text-gray-500">{t('myEmpty')}</p>;
  }

  return (
    <>
      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      <ul className="space-y-4">
        {items.map((r) => (
          <li key={r.id} className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <Link
                to={`/activity/${r.activity.slug}`}
                className="font-semibold text-gray-900 hover:text-primary text-sm"
              >
                {title(r)}
              </Link>
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusClass(r.status)}`}>
                {statusLabel(r.status)}
              </span>
            </div>
            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{r.content}</p>
            {r.status === 'rejected' && r.rejectionReason && (
              <p className="text-xs text-red-700 mt-2">
                {t('rejectedReason', { reason: r.rejectionReason })} {t('resubmitHint')}
              </p>
            )}
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-400">
                {formatDate(r.updatedAt, i18n.language)}
              </span>
              <div className="flex gap-3 text-sm">
                {/* Edit only within the 90-day window (R17); delete is always allowed (R10). */}
                {r.editable && (
                  <button
                    onClick={() => setEditing(r)}
                    disabled={busyId === r.id}
                    className="text-primary hover:underline font-medium disabled:opacity-50"
                  >
                    {t('editCta')}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(r)}
                  disabled={busyId === r.id}
                  className="text-red-600 hover:underline font-medium disabled:opacity-50"
                >
                  {t('deleteCta')}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {pickItems.length > 0 && (
        <>
          <h3 className="mt-8 mb-3 text-base font-semibold text-gray-900">{t('picks:name')}</h3>
          <ul className="space-y-4">
            {pickItems.map((r) => (
              <li key={r.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <Link
                    to={`/picks/${r.pick.slug}`}
                    className="font-semibold text-gray-900 hover:text-primary text-sm"
                  >
                    {localize(r.pick.titleCN, r.pick.titleEN, i18n.language)}
                  </Link>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusClass(r.status)}`}>
                    {statusLabel(r.status)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {r.verdict === 'RECOMMEND'
                    ? t('picks:detail.recommend')
                    : t('picks:detail.notRecommend')}
                </p>
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{r.content}</p>
                {r.status === 'rejected' && r.rejectionReason && (
                  <p className="text-xs text-red-700 mt-2">
                    {t('rejectedReason', { reason: r.rejectionReason })} {t('resubmitHint')}
                  </p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-gray-400">
                    {formatDate(r.updatedAt, i18n.language)}
                  </span>
                  <div className="flex gap-3 text-sm">
                    {/* A review can be edited while its Pick is still published. */}
                    {r.pick.status === 'PUBLISHED' && (
                      <button
                        onClick={() => setEditingPick(r)}
                        disabled={busyId === r.id}
                        className="text-primary hover:underline font-medium disabled:opacity-50"
                      >
                        {t('editCta')}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeletePick(r)}
                      disabled={busyId === r.id}
                      className="text-red-600 hover:underline font-medium disabled:opacity-50"
                    >
                      {t('deleteCta')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <PickReviewSheet
        open={editingPick !== null}
        pickId={editingPick?.pick.id ?? ''}
        pickType={editingPick?.pick.type ?? 'PROVIDER'}
        existing={editingPick}
        onClose={() => setEditingPick(null)}
        onSaved={() => {
          setEditingPick(null);
          load();
          onChanged?.();
        }}
      />

      {editing && (
        <ReviewEditorModal
          activityId={editing.activity.id}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            onChanged?.();
          }}
        />
      )}
    </>
  );
}
